import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query'
import {
  getUserInstructions,
  getWorkspaceInstructions,
  updateUserInstructions,
  updateWorkspaceInstructions,
  type CopilotInstructionsDocument,
  type UpdateUserInstructionsPayload,
  type UpdateWorkspaceInstructionsPayload
} from 'shared/api/copilot'

export const USER_INSTRUCTIONS_QUERY_KEY = 'copilot-user-instructions'
export const WORKSPACE_INSTRUCTIONS_QUERY_KEY = 'copilot-workspace-instructions'

export const useUserInstructions = (
  clientId: number,
  userId: number | undefined,
  enabled: boolean
): UseQueryResult<CopilotInstructionsDocument> => {
  return useQuery({
    queryKey: [USER_INSTRUCTIONS_QUERY_KEY, clientId, userId],
    queryFn: async () => await getUserInstructions(clientId),
    enabled: enabled && Boolean(clientId) && userId != null,
    retry: false,
    throwOnError: false
  })
}

export const useUpdateUserInstructions = (
  clientId: number,
  userId: number | undefined
): UseMutationResult<CopilotInstructionsDocument, Error, UpdateUserInstructionsPayload> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload) => await updateUserInstructions(clientId, payload),
    onSuccess: async (data) => {
      queryClient.setQueryData([USER_INSTRUCTIONS_QUERY_KEY, clientId, userId], data)
      await queryClient.invalidateQueries({ queryKey: [USER_INSTRUCTIONS_QUERY_KEY, clientId, userId] })
    }
  })
}

export const useWorkspaceInstructions = (
  clientId: number,
  enabled: boolean
): UseQueryResult<CopilotInstructionsDocument> => {
  return useQuery({
    queryKey: [WORKSPACE_INSTRUCTIONS_QUERY_KEY, clientId],
    queryFn: async () => await getWorkspaceInstructions(clientId),
    enabled: enabled && Boolean(clientId),
    retry: false,
    throwOnError: false
  })
}

export const useUpdateWorkspaceInstructions = (
  clientId: number
): UseMutationResult<CopilotInstructionsDocument, Error, UpdateWorkspaceInstructionsPayload> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload) => await updateWorkspaceInstructions(clientId, payload),
    onSuccess: async (data) => {
      queryClient.setQueryData([WORKSPACE_INSTRUCTIONS_QUERY_KEY, clientId], data)
      await queryClient.invalidateQueries({ queryKey: [WORKSPACE_INSTRUCTIONS_QUERY_KEY, clientId] })
    }
  })
}
